import { LoaderService } from './../../../shared/services/loader/loader.service';
import { Component, HostListener, ChangeDetectorRef, NgZone, OnDestroy } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable, Subject, take, takeUntil } from 'rxjs';
import { sessionAuthData, sessionUserData } from 'src/app/shared/interfaces/states-data';
import { AuthService } from 'src/app/shared/services/auth/auth.service';
import { TokenRefreshSchedulerService } from 'src/app/shared/services/auth/token-refresh-scheduler.service';
import { TokenRefreshCoordinatorService } from 'src/app/shared/services/auth/token-refresh-coordinator.service';
import { classifyCrossTabAuthChange } from 'src/app/shared/services/auth/cross-tab-auth-change';
import { getSessionAuthData } from 'src/app/shared/states/session-auth/session-auth.selectors';
import { JwtHelperService } from '@auth0/angular-jwt';
import { DateTime, Duration } from "luxon";
import { environment } from '../../../../environments/environment';
import { __clearSessionAuth, __updateSessionAuth } from 'src/app/shared/states/session-auth/session-auth.actions';
import { Router, RouterModule } from '@angular/router';
import { getSessionUserData } from 'src/app/shared/states/session-user/session-user.selectors';
import { __clearSessionUser, __updateSessionUser } from 'src/app/shared/states/session-user/session-user.actions';
import { SharedModule } from 'src/app/shared/shared.module';
import { WhiteLabelService } from './../../../shared/services/white-label/white-label.service';
import { syncSentryUser } from 'src/app/shared/config/sentry.config';
import { SyncOrchestratorService } from 'src/app/shared/services/offline-validation/sync-orchestrator.service';
import { OfflineValidationService } from 'src/app/shared/services/offline-validation/offline-validation.service';

@Component({
  standalone: true,
  imports: [SharedModule, RouterModule],
  selector: 'app-root',
  templateUrl: './app.component.html',
})
export class AppComponent implements OnDestroy {

  isLoading: Observable<boolean>;
  protected ngUnsubscribe: Subject<void> = new Subject<void>();

  debugActive = false;

  // Auth States
  sessionAuthState: sessionAuthData = { loggedIn: false };
  sessionExist = false;

  // User States
  sessionUserState: sessionUserData = {};

  // Json de tokens
  accessTokenData: any = null;
  refreshTokenData: any = null;

  // Intervalos de tiempo (panel de debug con F9)
  accessTokenRT = '';
  refreshTokenRT = '';
  updateSessionRT = '';
  accessTokenID = ''; // Initial Duration
  refreshTokenID = ''; // Initial Duration

  constructor(
    private store: Store<any>,
    private authService: AuthService,
    private jwtHelper: JwtHelperService,
    private _router: Router,
    private _loader: LoaderService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private whiteLabelService: WhiteLabelService,
    private tokenRefreshScheduler: TokenRefreshSchedulerService,
    private syncOrchestrator: SyncOrchestratorService,
    private offlineValidation: OfflineValidationService,
    private tokenRefreshCoordinator: TokenRefreshCoordinatorService,
  ) {


    /**
     * Active Loading when resolveFn execute
     */
    this.isLoading = this._loader.isLoading.asObservable();

    console.warn('%c -------> Contructor App', 'color: white');

    // Inicializar Marca Blanca
    this.whiteLabelService.init();

    // Estado guardado en local storage
    this.sessionAuthState = JSON.parse(localStorage.getItem('sessionAuthState') || JSON.stringify(this.sessionAuthState));
    this.sessionUserState = JSON.parse(localStorage.getItem('sessionUserState') || JSON.stringify(this.sessionUserState));

    // Guardar en reducer el estado del local storage
    this.store.dispatch(__updateSessionAuth(this.sessionAuthState));
    this.store.dispatch(__updateSessionUser(this.sessionUserState));
    console.log(1)
    // Al recibir cambios del reducer - Auth
    this.store.pipe(
      select(getSessionAuthData),
      takeUntil(this.ngUnsubscribe)
    ).subscribe((data: sessionAuthData) => {

      // console.warn(`%c -------> loggedIn: ${data.loggedIn}`, 'color: white');

      // Solo LECTURA del store: la persistencia en localStorage la hace el
      // reducer (`session-auth.reducers.ts`), único escritor por la vía del store.

      // Se guarda en el componente local el estado obtenido
      this.sessionAuthState = data;

      // Se desencriptan los tokens
      this.refreshTokenData = this.sessionAuthState.refresh_token ? this.jwtHelper.decodeToken(this.sessionAuthState.refresh_token || '') : null;
      this.accessTokenData = this.sessionAuthState.access_token ? this.jwtHelper.decodeToken(this.sessionAuthState.access_token || '') : null;

      // Si el token se desencripta correctamente
      if (!this.sessionExist && this.accessTokenData && this.refreshTokenData) {
        // console.warn('%c -------> Session Exist', 'color: white');
        this.sessionExist = true;
      }

      // Se detecta si otro componente dispara el action de __clearSessionAuth
      // ya que en el componente actual this.sessionExist seguiria manteniendo
      // un valor de sesion por lo que se procede a eliminar la sesion local
      if (!this.sessionAuthState.loggedIn && this.sessionExist) {
        console.warn('%c -------> Logout Condition', 'color: white');
        this.clearSession();
      }
    });

    // Al recibir cambios del reducer - User
    this.store.pipe(select(getSessionUserData), takeUntil(this.ngUnsubscribe)).subscribe((data: sessionUserData) => {

      // Se guarda en local storage el estado obtenido
      localStorage.setItem('sessionUserState', JSON.stringify(data));

      // Se guarda en el componente local el estado obtenido
      this.sessionUserState = data;
      syncSentryUser(data);

    });

    // B-SEC-005: si ya hay sesión válida en localStorage al bootstrap (refresh
    // de página o re-apertura del tab), arrancar el scheduler proactivo con el
    // access_token actual. Sin esto, el timer queda dormido hasta el próximo
    // login. Reemplaza el polling buggy basado en setInterval(333ms) que
    // dependía del flag `updatingSession` y podía quedar atascado.
    if (this.sessionAuthState?.access_token) {
      this.tokenRefreshScheduler.scheduleFor(this.sessionAuthState.access_token);
    }

    // C2-FRO-002 Fase 4 — bootstrap del SyncOrchestrator. Una sola vez por
    // sesión de la app. Recupera rows huérfanos en `syncing` (tab muerta
    // mid-process), limpia cache vencido y arranca el drain de la cola si
    // hay pendientes y conexión. Fire-and-forget: no bloquea el bootstrap.
    //
    // Orden importante: recoverOrphanedSyncing ANTES de initialize() para que
    // el primer triggerSync interno del orchestrator vea los rows rescatados.
    void this.offlineValidation
      .recoverOrphanedSyncing()
      .then((n) => {
        if (n > 0) {
          console.info(`[C2-FRO-002] recovered ${n} orphan syncing row(s)`);
        }
      })
      .catch((e) => console.warn('[C2-FRO-002] recoverOrphanedSyncing error:', e));
    void this.offlineValidation.cleanupExpired().catch((e) =>
      console.warn('[C2-FRO-002] cleanupExpired error:', e),
    );
    this.syncOrchestrator.initialize();

    // Sincronización de sesión entre pestañas: el evento `storage` dispara SOLO
    // en las OTRAS pestañas cuando cambia localStorage. Si otra pestaña cierra
    // sesión, esta replica el logout al instante; si otra pestaña rota tokens o
    // inicia sesión, esta adopta ese estado en su store para no quedarse con
    // tokens viejos en memoria.
    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('storage', this.onCrossTabAuthChange);
    });
  }

  ngOnDestroy(): void {
    window.removeEventListener('storage', this.onCrossTabAuthChange);
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Sincronización multi-pestaña de la sesión.
   *
   *  - Logout en otra pestaña (`sessionAuthState` borrado o `loggedIn:false`):
   *    replicamos el logout local (cancelar el refresh proactivo, resetear el
   *    coordinator, limpiar el store, ir al login). La guarda `!this.sessionExist`
   *    corta el ping-pong: una vez deslogueada, la pestaña ignora los eventos de
   *    logout que ella misma provoca en las demás.
   *  - Rotación o login en otra pestaña: el store de ESTA pestaña adopta el par
   *    nuevo y reprograma su timer proactivo. Antes esto se ignoraba y el store
   *    se quedaba con los tokens viejos; cualquier escritura posterior de ese
   *    store a localStorage resucitaba un refresh_token ya rotado y el backend
   *    respondía REFRESH_TOKEN_REUSED → logout de todas las pestañas. Ese era el
   *    "me saca de la sesión al cambiar de pestaña".
   *
   * Se relee localStorage al manejar el evento (no `event.newValue`): el evento
   * llega en diferido y la otra pestaña pudo haber escrito de nuevo entremedio.
   */
  private onCrossTabAuthChange = (event: StorageEvent) => {
    if (event.key === 'sessionUserState') {
      this.syncUserStateFromStorage();
      return;
    }
    if (event.key !== 'sessionAuthState') return;

    const change = classifyCrossTabAuthChange(
      this.readStorage('sessionAuthState'),
      this.sessionAuthState.access_token,
    );
    if (change.kind === 'ignore') return;

    if (change.kind === 'logout') {
      if (!this.sessionExist) return;
      this.ngZone.run(() => {
        // Logout propagado desde otra pestaña: también es intencional → suprime
        // el modal de "Sesión expirada" ante los 401 residuales de esta pestaña.
        this.tokenRefreshCoordinator.markIntentionalLogout();
        this.sessionExist = false;
        this.tokenRefreshScheduler.cancel();
        this.tokenRefreshCoordinator.reset();
        this.store.dispatch(__clearSessionAuth());
        this.store.dispatch(__clearSessionUser());
        this._router.navigate(['/auth/login']);
      });
      return;
    }

    // `sync`: adoptar el par rotado / la sesión nueva. El reducer vuelve a
    // escribir el mismo valor en localStorage (idempotente, sin evento `storage`).
    this.ngZone.run(() => {
      this.store.dispatch(__updateSessionAuth(change.state));
      if (change.state.access_token) {
        this.tokenRefreshScheduler.scheduleFor(change.state.access_token);
      }
    });
  };

  /**
   * Si otra pestaña cambió el usuario de la sesión (login con otra cuenta,
   * impersonación), el store local adopta esos datos. Se compara por `user_id` y
   * `data_user_hash` para no despachar en cada escritura idéntica.
   */
  private syncUserStateFromStorage(): void {
    const raw = this.readStorage('sessionUserState');
    if (!raw) return;
    let latest: sessionUserData | null;
    try {
      latest = JSON.parse(raw);
    } catch {
      return;
    }
    if (!latest || typeof latest !== 'object') return;
    const sameUser =
      (latest.user_id ?? '') === (this.sessionUserState.user_id ?? '') &&
      (latest.data_user_hash ?? '') === (this.sessionUserState.data_user_hash ?? '');
    if (sameUser) return;
    this.ngZone.run(() => {
      this.store.dispatch(__updateSessionUser(latest as sessionUserData));
    });
  }

  private readStorage(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  /* Al presionar escape cerrar el sidebar */
  @HostListener('document:keydown.f9')
  onKeydownHandler() {
    this.debugActive = !this.debugActive;
  }

  /**
   * B-SEC-005 refresh proactivo (moved): el refresh del access_token ya NO se hace
   * desde acá con polling 333ms. Vive en `TokenRefreshSchedulerService` que se
   * programa basándose en el `exp` del JWT (setTimeout exacto), evitando:
   *   - El flag `updatingSession` que podía quedar atascado en `true` tras un
   *     refresh fallido, deteniendo todos los refresh posteriores.
   *   - Race condition entre 2 callers (interceptor reactivo + polling) llamando
   *     a /refresh-token con el mismo refresh_token y disparando REPLAY_DETECTED.
   *   - 333ms de polling permanente innecesario (1 setTimeout es suficiente).
   *
   * El polling de cookies legacy de goliiive.com (`access_token` / `refresh_token`
   * / `logout_session`) que quedaba acá también se eliminó: nada las escribe hoy
   * (ni el Laravel viejo ni este frontend) y, de existir, cada 333 ms habrían
   * pisado en el store el par recién rotado con el par original de la cookie,
   * dejando la sesión en un loop permanente de 401 + refresh. El handoff desde
   * el sistema viejo entra por `POST /user/login-laravel-token`.
   */

  /* Limpiar la sesion de la aplicacion */
  clearSession() {

    console.warn('%c -------> clearSession()', 'color: white');

    // La sesion no existe
    this.sessionExist = false;

    // Borrar sesion del reducer
    this.store.dispatch(__clearSessionAuth());
    syncSentryUser({});

    // Navegar al login
    this._router.navigate(['auth']);
  }
}
